/**
 * Suite identifiers, parsers and prefix encoding for the Legacy Architect
 * crypto-agility protocol.
 *
 * See: docs/crypto/ENCRYPTION-ARCHITECTURE.md §3
 *      docs/durability/CRYPTO-AGILITY-PROTOCOL.md §2-§3
 *      docs/decisions/ADR-002-encryption-model.md
 *
 * Every encrypted artifact at rest in this system is stored in the form
 *
 *     v{N}:{suite-id}:{base64url-payload}
 *
 * The version prefix is mandatory and immutable: it lets a recovery tool
 * decades from now identify which cipher suite produced an artifact without
 * any database access. Do not strip, mutate or invent suite identifiers.
 */
/**
 * The currently active suite identifier.
 *
 * NEVER inline this string elsewhere — always import this constant. Any
 * future migration (v2 hybrid post-quantum suite, etc.) flips this value
 * in a single place.
 */
export const ACTIVE_SUITE = 'v1:hpke-x25519-sha256-aes256gcm';
/**
 * The set of suite identifiers this build is willing to read. Writes always
 * use {@link ACTIVE_SUITE}. Reads tolerate any deprecated-but-still-supported
 * suite that still has a registered decoder. When a suite reaches the
 * "sunset" lifecycle stage (per CRYPTO-AGILITY-PROTOCOL.md §6.3) it must be
 * removed from this set.
 */
export const SUPPORTED_SUITES = new Set([
    'v1:hpke-x25519-sha256-aes256gcm',
]);
// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
/**
 * Thrown when an artifact carries a suite identifier this build does not
 * recognise. This is a clear, recoverable signal: the artifact predates this
 * build (so a sunset suite was removed), or comes from a future version.
 *
 * @see {@link SUPPORTED_SUITES}
 */
export class SuiteUnknownError extends Error {
    name = 'SuiteUnknownError';
    suiteId;
    constructor(suiteId, detail) {
        const tail = detail !== undefined && detail.length > 0 ? ` (${detail})` : '';
        super(`Unknown crypto suite '${suiteId}'. Supported: ${[...SUPPORTED_SUITES].join(', ')}${tail}`);
        this.suiteId = suiteId;
    }
}
/**
 * Thrown when a suite-prefixed artifact is malformed: missing version,
 * empty payload, non-base64url payload, etc.
 */
export class SuiteParseError extends Error {
    name = 'SuiteParseError';
    constructor(input, reason) {
        const sample = input.length > 64 ? `${input.slice(0, 64)}...` : input;
        super(`Malformed suite-prefixed artifact ('${sample}'): ${reason}`);
    }
}
// ---------------------------------------------------------------------------
// Base64url helpers (RFC 4648 §5, no padding)
// ---------------------------------------------------------------------------
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]*$/;
// Portable base64url (RFC 4648 §5, no padding) via the standard `btoa`/`atob`
// globals — present in both Node 20+ and browsers — so this module runs on the
// recipient's device with the exact same code the server uses. (Avoids Node's
// `Buffer`, which is undefined in the browser.)
function base64urlEncode(bytes) {
    let bin = '';
    for (let i = 0; i < bytes.byteLength; i++)
        bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function base64urlDecode(input) {
    if (!BASE64URL_PATTERN.test(input)) {
        throw new SuiteParseError(input, 'payload contains characters outside the base64url alphabet');
    }
    const standard = input.replace(/-/g, '+').replace(/_/g, '/');
    const padded = standard + '='.repeat((4 - (standard.length % 4)) % 4);
    let bin;
    try {
        bin = atob(padded);
    }
    catch {
        throw new SuiteParseError(input, 'payload is not valid base64url');
    }
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    // `atob` silently ignores some malformed trailing bits; verify round-trip to
    // detect corruption, matching the prior Buffer-based behaviour.
    if (base64urlEncode(out) !== input) {
        throw new SuiteParseError(input, 'payload is not valid base64url');
    }
    return out;
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Parse a suite identifier string into its components.
 *
 * The expected shape is `v{N}:hpke-{kem}-{kdf}-{aead}`. Any deviation throws
 * {@link SuiteParseError}. An identifier whose version cannot be decoded
 * throws {@link SuiteUnknownError} — callers must treat this as a fatal
 * condition for the artifact in question.
 *
 * @example
 *   parseSuiteId('v1:hpke-x25519-sha256-aes256gcm')
 *   // => { version: 1, kem: 'x25519', kdf: 'sha256', aead: 'aes256gcm' }
 */
export function parseSuiteId(id) {
    if (typeof id !== 'string' || id.length === 0) {
        throw new SuiteParseError(String(id), 'suite identifier is empty');
    }
    if (!SUPPORTED_SUITES.has(id)) {
        throw new SuiteUnknownError(id);
    }
    // 'v1:hpke-x25519-sha256-aes256gcm' -> ['v1', 'hpke-x25519-sha256-aes256gcm']
    const colonIndex = id.indexOf(':');
    if (colonIndex < 1 || colonIndex === id.length - 1) {
        throw new SuiteParseError(id, "missing 'v{N}:' version prefix");
    }
    const versionToken = id.slice(0, colonIndex);
    const suiteBody = id.slice(colonIndex + 1);
    if (!versionToken.startsWith('v')) {
        throw new SuiteParseError(id, "version token must start with 'v'");
    }
    const versionStr = versionToken.slice(1);
    if (!/^\d+$/.test(versionStr)) {
        throw new SuiteParseError(id, `version '${versionToken}' is not a positive integer`);
    }
    const version = Number.parseInt(versionStr, 10);
    if (!Number.isSafeInteger(version) || version < 1) {
        throw new SuiteParseError(id, `version '${versionToken}' is out of range`);
    }
    // The body is 'hpke-<kem>-<kdf>-<aead>'.
    const parts = suiteBody.split('-');
    if (parts.length !== 4 || parts[0] !== 'hpke') {
        throw new SuiteParseError(id, "suite body must be of the form 'hpke-<kem>-<kdf>-<aead>'");
    }
    const [, kem, kdf, aead] = parts;
    if (kem.length === 0 || kdf.length === 0 || aead.length === 0) {
        throw new SuiteParseError(id, 'suite body contains an empty token');
    }
    return { version, kem, kdf, aead };
}
/**
 * Wrap a raw ciphertext payload in the canonical suite-prefixed form
 * `${suiteId}:${base64url(payload)}` used by every persisted encrypted
 * artifact in this system.
 *
 * Validates that {@link suiteId} is recognised before returning so callers
 * cannot accidentally write artifacts under a typo'd or future suite.
 */
export function prefixWithSuite(suiteId, payload) {
    if (!SUPPORTED_SUITES.has(suiteId)) {
        throw new SuiteUnknownError(suiteId, 'cannot prefix with an unrecognised suite');
    }
    if (!(payload instanceof Uint8Array)) {
        throw new SuiteParseError(suiteId, 'payload must be a Uint8Array');
    }
    if (payload.byteLength === 0) {
        throw new SuiteParseError(suiteId, 'payload must not be empty');
    }
    return `${suiteId}:${base64urlEncode(payload)}`;
}
/**
 * Inverse of {@link prefixWithSuite}. Splits a stored artifact back into
 * its declared suite and raw payload bytes. Throws if the suite is unknown
 * (so the caller cannot silently treat a v2 artifact as v1) or if the
 * payload is not valid base64url.
 */
export function parseSuitePrefixed(value) {
    if (typeof value !== 'string' || value.length === 0) {
        throw new SuiteParseError(String(value), 'input is empty');
    }
    // Suite identifiers contain exactly two colons:
    //   v1 :  hpke-x25519-sha256-aes256gcm  :  <base64url>
    // Split on the LAST colon so the suiteId is everything before it.
    const lastColon = value.lastIndexOf(':');
    if (lastColon < 0) {
        throw new SuiteParseError(value, "missing ':' separator before payload");
    }
    const suiteId = value.slice(0, lastColon);
    const payloadStr = value.slice(lastColon + 1);
    if (suiteId.length === 0) {
        throw new SuiteParseError(value, 'suite identifier is empty');
    }
    if (payloadStr.length === 0) {
        throw new SuiteParseError(value, 'payload is empty');
    }
    if (!SUPPORTED_SUITES.has(suiteId)) {
        throw new SuiteUnknownError(suiteId);
    }
    const payload = base64urlDecode(payloadStr);
    return { suiteId, payload };
}
/**
 * Helper for callers who want to verify a suite is acceptable for read
 * without parsing the whole artifact. Returns true iff `suiteId` is in
 * {@link SUPPORTED_SUITES}.
 */
export function isSupportedSuite(suiteId) {
    return SUPPORTED_SUITES.has(suiteId);
}
